import { useState } from "react";
import { supabase } from "../supabaseClient";
import { FiLock, FiEye, FiEyeOff, FiShield, FiCheck, FiAlertCircle, FiX } from "react-icons/fi";
import { getPasswordStrength } from "../utils/passwordStrength";
import "../assets/css/settings-pages.css";

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordStrength = getPasswordStrength(newPassword);

  const showError = (message) => {
    setPasswordError(message);
    setTimeout(() => setPasswordError(""), 3000);
  };

  const dismissError = () => {
    setPasswordError("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);
    setIsUpdating(true);

    if (newPassword !== confirmPassword) {
      showError("New password and confirm password do not match.");
      setIsUpdating(false);
      return;
    }

    if (newPassword.length < 8) {
      showError("Password must be at least 8 characters long.");
      setIsUpdating(false);
      return;
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) {
      showError("Failed to get user information.");
      setIsUpdating(false);
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });

    if (signInError) {
      showError("Current password is incorrect.");
      setIsUpdating(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      showError(error.message);
      setIsUpdating(false);
    } else {
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setIsUpdating(false);
      setTimeout(() => setPasswordSuccess(false), 3000);
    }
  };

  return (
    <div className="settings-page-container">
      {/* Stats Bar - Security Info */}
      <div className="settings-stats-bar">
        <div className="settings-stat-card">
          <div className="settings-stat-icon teal">
            <FiShield />
          </div>
          <div className="settings-stat-info">
            <h3 style={{ fontSize: "18px" }}>Password</h3>
            <p>Keep your account secure</p>
          </div>
        </div>
        <div className="settings-stat-card">
          <div className="settings-stat-icon orange">
            <FiLock />
          </div>
          <div className="settings-stat-info">
            <h3 style={{ fontSize: "18px" }}>8+ chars</h3>
            <p>Minimum length required</p>
          </div>
        </div>
      </div>

      {/* Password Form Card */}
      <div className="settings-section-card">
        <div className="settings-section-header">
          <div className="settings-section-title">
            <FiLock style={{ color: "var(--teal-bg)" }} />
            <h5>Update Your Password</h5>
          </div>
        </div>
        <div className="settings-section-body">
          {/* Alert Messages */}
          {passwordError && (
            <div className="settings-alert settings-alert-error">
              <div className="settings-alert-content">
                <FiAlertCircle size={18} />
                {passwordError}
              </div>
              <button className="settings-alert-close" onClick={dismissError}>
                <FiX size={18} />
              </button>
            </div>
          )}

          {passwordSuccess && (
            <div className="settings-alert settings-alert-success">
              <div className="settings-alert-content">
                <FiCheck size={18} />
                Password changed successfully!
              </div>
            </div>
          )}

          <form onSubmit={handleChangePassword}>
            {/* Current Password */}
            <div className="settings-form-group">
              <label className="settings-form-label">Current Password</label>
              <div className="settings-input-wrapper">
                <FiLock className="settings-input-icon" />
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter your current password"
                  className="settings-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="settings-input-toggle"
                >
                  {showCurrentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="settings-form-group">
              <label className="settings-form-label">New Password</label>
              <div className="settings-input-wrapper">
                <FiLock className="settings-input-icon" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter your new password"
                  className="settings-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="settings-input-toggle"
                >
                  {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="settings-password-strength">
                  <div className="settings-strength-bars">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`settings-strength-bar ${level <= passwordStrength.strength ? "active" : ""}`}
                        style={{
                          "--strength-color": passwordStrength.color,
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="settings-strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="settings-form-group">
              <label className="settings-form-label">Confirm New Password</label>
              <div className="settings-input-wrapper">
                <FiLock className="settings-input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your new password"
                  className="settings-input"
                  style={{
                    borderColor:
                      confirmPassword && newPassword !== confirmPassword
                        ? "var(--danger)"
                        : confirmPassword && newPassword === confirmPassword
                        ? "var(--teal-bg)"
                        : undefined,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="settings-input-toggle"
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>

              {confirmPassword && newPassword !== confirmPassword && (
                <div className="settings-match-indicator error">
                  <FiX size={14} />
                  Passwords do not match
                </div>
              )}
              {confirmPassword && newPassword === confirmPassword && (
                <div className="settings-match-indicator success">
                  <FiCheck size={14} />
                  Passwords match
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isUpdating}
              className="settings-btn-primary"
            >
              {isUpdating && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              {isUpdating ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>

      {/* Security Tips */}
      <div className="settings-info-box">
        <h4>
          <FiShield />
          Password Tips
        </h4>
        <ul>
          <li>Use at least 8 characters</li>
          <li>Include uppercase and lowercase letters</li>
          <li>Add numbers and special characters</li>
          <li>Avoid using personal information</li>
        </ul>
      </div>
    </div>
  );
}

export default ChangePassword;
